import { Box, Button, Divider, Flex, Skeleton, Spinner, Stack, Text } from "@chakra-ui/react";
import {
  Icons,
  SkeletonCard,
  ToolTip,
  Typography,
  useCurrencyFormatter,
  useFlowBuilderSession,
} from "@reconvert/react-ui-component";
import {
  MutableRefObject,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectAppState, toggleHelpModal } from "../../../app/slice/appSlice";
import { Overview, selectConditionsState, setOverview } from "../../../app/slice/conditionSlice";
import useConditions from "../../../hooks/useConditions";
import ConditionCard from "../../ConditionCard";
import { Condition, ConditionRule, Platform } from "../../../models";
import { APP_PLATFORM, RECONVERT_IO_HELP_URL } from "../../../config";
import ConditionTooltip from "./ConditionTooltip";
import useUnsavedBar from "../../../hooks/useUnsavedBar";
import { ConditionFact, getConditionText } from "@reconvert/reconvert-utils";
import type { Modifier } from "@popperjs/core";
import { ConditionType } from "../../../hooks/useAndOrGrouping";
import OrSeprator from "./AndOrGrouping/OrSeprator";
import AndSeprator from "./AndOrGrouping/AndSeprator";
import { PageNames } from "../../../app/slice/appSlice.model";
import { sendPostMessage, IFramePostMessageType, AIChatbotPostMessageEventType} from "../../../utils/helpers";
import { BOTTOM_SHEET_TYPES, navigateTo, openBottomSheet } from "../../../app/slice/mobileSlice";
import { useConditionsDragAndDrop, useConditionDraggable } from "../../../hooks/useConditionsDragAndDrop";

/** Mobile: keep tooltip inside the viewport when the trigger sits near the right edge. */
const mobileConditionTooltipModifiers: Partial<Modifier<string, Record<string, unknown>>>[] = [
  {
    name: "preventOverflow",
    options: {
      rootBoundary: "viewport",
      altAxis: true,
      padding: 12,
    },
  },
];

const SkeletonComponent = () => (
  <Box display="grid" mb={"16px"} mr={3}>
    <SkeletonCard>
      <Stack gap="13px">
        <Flex justifyContent="space-between">
          <Flex gap="8px">
            <Skeleton startColor="gray.400" endColor="gray.400" height="20px" width="140px" />
            <Skeleton startColor="gray.400" endColor="gray.400" height="20px" width="35px" />
          </Flex>
          <Flex gap="8px">
            <Skeleton startColor="gray.400" endColor="gray.400" height="24px" width="24px" />
            <Skeleton startColor="gray.400" endColor="gray.400" height="24px" width="24px" />
            <Skeleton startColor="gray.400" endColor="gray.400" height="24px" width="24px" />
          </Flex>
        </Flex>
        <Skeleton startColor="gray.400" endColor="gray.400" height="12px" width="252px" />
      </Stack>
    </SkeletonCard>
  </Box>
);

const Conditions = () => {
  const dispatch = useAppDispatch();
  const {
    loading: isConditionDataLoading,
    overview,
    conditions,
    selectedConditionId,
    selectedVariantId,
  } = useAppSelector(selectConditionsState);
  const { currencyFormatter } = useCurrencyFormatter();
  const { storeInfo, activePage, isMobile } = useAppSelector(selectAppState);
  const { updateConditionStatus, setSelectedConditionId, updateConditionPriority } = useConditions();
  const { t } = useTranslation();
  const { preventAction } = useUnsavedBar();
  const { setSelectedConditionInSession, setSelectedVariantInSession } = useFlowBuilderSession();
  const { dragState, conditionsContainerRef } = useConditionsDragAndDrop({
    conditions,
    updateConditionPriority,
  });

  const defaultCondition = useMemo(() => {
    return conditions.filter(condition => condition.isDefault);
  }, [conditions]);

  const draggableConditions = useMemo(() => {
    return conditions.filter(condition => !condition.isDefault);
  }, [conditions]);

  const mouseRef = useRef<any>();

  const formatCurrency = useCallback(
    (value: number) => {
      return currencyFormatter(value, storeInfo?.currency!);
    },
    [currencyFormatter, storeInfo?.currency],
  );

  useEffect(() => {
    if (APP_PLATFORM === Platform.Shopify) {
      sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, { conditionId: selectedConditionId || "", eventType: AIChatbotPostMessageEventType.SET_SELECTED_CONDITION });
    } else {
      selectedConditionId && setSelectedConditionInSession(selectedConditionId);
    }
  }, [selectedConditionId]);

  useEffect(() => {
    if (APP_PLATFORM === Platform.Shopify) {
      sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, { variantId: selectedVariantId || undefined, eventType: AIChatbotPostMessageEventType.SET_SELECTED_VARIANT });
    } else {
      selectedVariantId && setSelectedVariantInSession(selectedVariantId);
    }
  }, [selectedVariantId]);

  useEffect(() => {
    const defaultConditionCard = document?.getElementById("defaultCondition");

    if (defaultConditionCard) {
      conditionsContainerRef?.current?.scrollTo({
        top: conditionsContainerRef?.current?.scrollHeight,
        behavior: "smooth",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddConditionClick = useCallback(() => {
    if (isMobile) {
      dispatch(openBottomSheet({ type: BOTTOM_SHEET_TYPES.ADD_CONDITION }));
      return;
    }

    dispatch(setOverview(Overview.ADD_CONDITION_VIEW));
    conditionsContainerRef?.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [dispatch, isMobile]);

  const handleConditionClick = (conditionId: string) => {
    preventAction(() => {
      setSelectedConditionId(conditionId);
      if (isMobile) {
        setSelectedConditionInSession(conditionId);
        dispatch(navigateTo("slots"));
      }
    });
  };

  return (
    <Box
      sx={{
       flexBasis: 96,
       h: "100%",
       py: isMobile ? 4 : 7,
       borderRight: isMobile ? "none" : "1px solid #E4E8EE",
       ...(isMobile && { display: "flex", flexDirection: "column", overflow: "hidden" }),
      }}
    >
      <Flex justifyContent={"space-between"} flexDirection="column" pr={4} flexShrink={0}>
        <Flex justifyContent={"space-between"} w="100%" flexDirection={"column"} gap="2">
          <Flex alignItems={"center"}>
            <Typography variant="h2-r-medium" pr={2}>
              {t("Conditions")}
            </Typography>
            <ToolTip
              trigger={isMobile ? "click" : "hover"}
              placement="bottom"
              label={t(
                "Conditions allow you to segment your shoppers and display personalized {{pageName}} pages based on customer properties and actions.",
                { pageName: t(PageNames[activePage!]!).replace("page", "").toLowerCase() },
              )}
              maxWidth={"285px"}
            >
              <span>
                <Icons.Question color={"neutrals.700"} />
              </span>
            </ToolTip>
          </Flex>
          <Flex my="auto">
            <Typography
              as="a"
              variant="small"
              color="link.color"
              fontWeight={500}
              className={APP_PLATFORM === Platform.Wix ? "link" : " "}
              cursor="pointer"
              onClick={() => {
                const link =
                  APP_PLATFORM === Platform.Shopify
                    ? `${RECONVERT_IO_HELP_URL}/en/article/how-do-conditions-work-12yqfsv/reader/?bust=1706881668418`
                    : `${RECONVERT_IO_HELP_URL}/en/article/how-do-conditions-work-wix-unpozd/`;

                dispatch(
                  toggleHelpModal({
                    link: link,
                    title: t("How do conditions work?").toString(),
                  }),
                );
              }}
            >
              {t("How do conditions work?")}
            </Typography>
          </Flex>
        </Flex>
      </Flex>

      <Box pr={isMobile ? 0 : 4} mb="16px" flexShrink={0}>
        <Divider orientation="horizontal" my="16px" />
        <Button variant="secondary" w="100%" onClick={() => preventAction(() => handleAddConditionClick())}>
          <Typography variant="subheading2" color="neutrals.900" fontWeight={500} lineHeight="20px" mr="8px">
            {t("Add Condition")}
          </Typography>
          <Icons.Plus cursor="pointer" height={"16px"} width={"16px"} />
        </Button>
      </Box>

      <Box
        mt={4}
        textAlign="center"
        overflowY="auto"
        {...(isMobile
          ? { flex: 1, minHeight: 0 }
          : { height: `calc(100vh - ${APP_PLATFORM === Platform.Wix ? "240px" : "200px"})` })}
        ref={conditionsContainerRef}
      >
        {overview === Overview.ADD_CONDITION_VIEW && <SkeletonComponent />}

        {isConditionDataLoading ? (
          <Spinner variant={"ring"} size={"lg"} mt={20} />
        ) : (
          <>
            {!conditions || conditions.length === 0 ? (
              <ConditionCard
                title={t("Default condition")}
                description="Horem ipsum dolor sit amet, consectetur adipiscing elit."
                isDisabled={false}
                isSelected={false}
                isConditionOn={false}
                p={0}
              />
            ) : (
              <>
                <Box>
                  {draggableConditions.map((con, index) => {
                    const isDragging = dragState?.draggedConditionId === con.id;

                    const shouldAddSpaceBefore =
                      dragState &&
                      dragState.destinationIndex !== dragState.sourceIndex &&
                      dragState.destinationIndex < dragState.sourceIndex &&
                      dragState.destinationIndex === index;

                    const shouldAddSpaceAfter =
                      dragState &&
                      dragState.destinationIndex !== dragState.sourceIndex &&
                      dragState.destinationIndex > dragState.sourceIndex &&
                      dragState.destinationIndex === index;

                    return (
                      <Box
                        key={con.id.toString()}
                        pt={shouldAddSpaceBefore ? `${dragState.draggedHeight}px` : 0}
                        pb={shouldAddSpaceAfter ? `${dragState.draggedHeight}px` : 0}
                        transition="padding 140ms ease"
                      >
                        <PragmaticDraggableConditionCard condition={con} index={index} isDragging={isDragging}>
                          {({ dragHandleProps, snapshot }) => (
                            <ConditionCard
                              conditionId={con?.id}
                              title={con?.name}
                              description={
                                con?.isDefault
                                  ? t(
                                      "Upsell.com displays the default condition when customers do not meet any other condition",
                                    )
                                  : con?.storeName
                              }
                              isDisabled={!con?.status}
                              isConditionOn={con?.status || false}
                              isSelected={selectedConditionId === con?.id}
                              onClick={() => handleConditionClick(con?.id)}
                              actions={[]}
                              p={2}
                              toggleCondition={updateConditionStatus}
                              condition={con}
                              dragHandleProps={dragHandleProps}
                              snapshot={snapshot}
                              alwaysShowDragHandle={isMobile || snapshot.isDragging}
                            >
                              <ConditionTextContainer con={con} mouseRef={mouseRef} formatCurrency={formatCurrency} />
                            </ConditionCard>
                          )}
                        </PragmaticDraggableConditionCard>
                      </Box>
                    );
                  })}
                </Box>

                <>
                  {defaultCondition.map(con => {
                    return (
                      <ConditionCard
                        key={con?.id}
                        conditionId={con?.id}
                        title={con?.name}
                        description={
                          con?.isDefault
                            ? t(
                                "Upsell.com displays the default condition when customers do not meet any other condition",
                              )
                            : con?.storeName
                        }
                        isDisabled={!con?.status}
                        isConditionOn={con?.status || false}
                        isSelected={selectedConditionId === con?.id}
                        onClick={() => handleConditionClick(con?.id)}
                        actions={[]}
                        p={2}
                        toggleCondition={updateConditionStatus}
                        condition={con}
                        id={"defaultCondition"}
                      ></ConditionCard>
                    );
                  })}
                </>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const PragmaticDraggableConditionCard = ({
  condition,
  index,
  isDragging,
  children,
}: {
  condition: Condition;
  index: number;
  isDragging: boolean;
  children: (props: {
    dragHandleProps: {
      ref: (element: HTMLDivElement | null) => void;
      onClick: (event: ReactMouseEvent<HTMLDivElement>) => void;
      sx: Record<string, string>;
    };
    snapshot: { isDragging: boolean };
  }) => JSX.Element;
}) => {
  const { cardRef, dragHandleProps, snapshot } = useConditionDraggable({
    condition,
    index,
    isDragging,
  });

  return (
    <Box
      ref={cardRef}
      data-condition-draggable="true"
      data-condition-id={condition.id}
      data-condition-index={index}
      opacity={isDragging ? 0.35 : 1}
      transition="opacity 120ms ease"
    >
      {children({
        dragHandleProps,
        snapshot,
      })}
    </Box>
  );
};

export const ConditionTextContainer = ({
  con,
  mouseRef,
  formatCurrency,
}: {
  con: Condition;
  mouseRef: MutableRefObject<any>;
  formatCurrency: (value: number) => string;
}) => {
  const conditionArray = con?.rule?.any;
  const flattenedCondition = conditionArray?.flatMap((item: any) => item.all);
  const flattenedConditionLength = flattenedCondition?.length;
  const [showedCondition, setShowedCondition] = useState(3);

  return (
    <>
      {flattenedCondition?.slice(0, showedCondition).map((item: any, index: number) => {
        return (
          <Box key={item?.id}>
            {index > 0 && item?.conditionType === ConditionType.OR && <OrSeprator />}
            {index > 0 && item?.conditionType === ConditionType.AND && <AndSeprator />}
            <ConditionText con={item} mouseRef={mouseRef} formatCurrency={formatCurrency} />
          </Box>
        );
      })}
      {flattenedConditionLength > showedCondition && (
        <Flex m={2}>
          <Typography
            fontStyle={"italic"}
            variant="body"
            onClick={e => {
              e.stopPropagation();
              setShowedCondition(flattenedConditionLength);
            }}
          >
            + {flattenedConditionLength - showedCondition} more conditions
          </Typography>
        </Flex>
      )}
    </>
  );
};

const ConditionText = ({
  con,
  mouseRef,
  formatCurrency,
}: {
  con: ConditionRule;
  mouseRef: MutableRefObject<any>;
  formatCurrency: (value: number) => string;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isMobile } = useAppSelector(selectAppState);

  const renderConditionInfo = (conditionRule: ConditionRule) => {
    switch (conditionRule?.fact) {
      case ConditionFact.SPECIFIC_PRODUCT:
        return (
          <>
            {conditionRule?.extraFields?.productIds?.length}{" "}
            {`product${conditionRule?.extraFields?.productIds?.length > 1 ? "s" : ""}`}{" "}
          </>
        );

      case ConditionFact.PRODUCT_COLLECTION:
        return (
          <>
            {conditionRule?.value?.length}{" "}
            {APP_PLATFORM === Platform.Shopify
              ? `collection${conditionRule?.value?.length > 1 ? "s" : ""}`
              : `${conditionRule?.value?.length === 1 ? "category" : "categories"}`}
          </>
        );

      default:
        break;
    }
  };

  return (
    <>
      <Flex gap="12px">
        <Divider orientation="vertical" height={"auto"} borderLeft={"2px solid"} borderLeftColor={"divider.2"} />
        <Typography 
          variant="body"
          sx={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "normal",
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html:
                getConditionText(con, formatCurrency, APP_PLATFORM as any, {
                  withAnnotation: true,
                  hideProductAndCollectionCount: true,
                }) || "",
            }}
          ></span>{" "}
          <ToolTip
            trigger={isMobile ? "click" : "hover"}
            placement={isMobile ? "bottom-end" : "right"}
            modifiers={isMobile ? mobileConditionTooltipModifiers : undefined}
            isOpen={isOpen}
            label={
              <ConditionTooltip
                conditionRule={con}
                setIsOpen={setIsOpen}
                mouseRef={mouseRef}
                isMobile={isMobile}
              />
            }
            pointerEvents={"all"}
          >
            <Text
              as="u"
              onMouseEnter={() => {
                mouseRef.current = false;
                setIsOpen(true);
              }}
              onMouseLeave={() => {
                setTimeout(() => {
                  if (!mouseRef.current) {
                    setIsOpen(false);
                  }
                }, 500);
              }}
            >
              {renderConditionInfo(con)}
            </Text>
          </ToolTip>
        </Typography>
      </Flex>
    </>
  );
};

export default Conditions;
